import { VariantProps, cva } from "class-variance-authority";
import { forwardRef, ComponentPropsWithoutRef } from "react";
import { Control, FieldValues, Path } from "react-hook-form";

import { twMerge } from "../utils/tailwind-utils";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./Form";

const inputVariants = cva("input", {
  variants: {
    size: {
      sm: ["tw-h-9", "tw-text-sm"],
      md: ["tw-h-10", "tw-text-base"],
      lg: ["tw-h-12", "tw-text-lg"],
      xl: ["tw-h-16", "tw-text-xl"],
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type InputProps = ComponentPropsWithoutRef<"input"> &
  VariantProps<typeof inputVariants>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, size, ...rest }: InputProps, ref) => (
    <input
      {...rest}
      className={twMerge(
        "tw-flex tw-h-10 tw-w-full tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-ring-offset-background file:tw-border-0 file:tw-bg-transparent file:tw-text-sm file:tw-font-medium placeholder:tw-text-muted-foreground focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50",

        inputVariants({ className, size }),
      )}
      ref={ref}
    />
  ),
);

interface StringInputProps<T extends FieldValues>
  extends Pick<InputProps, "className" | "placeholder" | "size"> {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
}

export const StringInput = <T extends FieldValues>({
  name,
  control,
  required = true,
  label,
  ...rest
}: StringInputProps<T>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({
        field: { value, ...restField },
        formState: { isSubmitting },
      }) => (
        <FormItem>
          {label && (
            <FormLabel>
              <span className="tw-font-bold">{label}</span>
              {required && <span className="tw-text-red-300"> *</span>}
            </FormLabel>
          )}
          <FormControl>
            <div className="tw-flex tw-flex-col">
              <Input
                {...rest}
                {...restField}
                value={value ?? ""}
                type="text"
                disabled={isSubmitting}
              />

              <FormMessage />
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
